<template>
  <transition name="tts-bar-fade">
    <div
      v-if="ttsStore.visible"
      ref="barEl"
      class="global-tts-bar"
      :class="{ dragging }"
      :style="barStyle"
      @pointerdown="onPointerDown"
    >
      <div class="tts-bar-inner">
        <!-- 拖动把手 -->
        <span class="tts-drag-handle" title="拖动调整位置">
          <el-icon><Rank /></el-icon>
        </span>

        <!-- 播放/暂停/合成中 -->
        <el-button
          class="tts-btn-main"
          circle
          type="primary"
          :loading="isSynthesizing"
          @click="onToggle"
        >
          <el-icon v-if="!isSynthesizing">
            <VideoPause v-if="ttsStore.phase === 'playing'" />
            <VideoPlay v-else />
          </el-icon>
        </el-button>

        <!-- 进度区 -->
        <div class="tts-progress">
          <div class="tts-progress-head">
            <span class="tts-seg-text" :title="ttsStore.segText">
              {{ ttsStore.segText || (isSynthesizing ? '正在合成语音…' : '准备播放') }}
            </span>
            <span class="tts-time">{{ fmt(ttsStore.currentTime) }} / {{ fmt(ttsStore.duration) }}</span>
          </div>
          <el-slider
            class="tts-slider"
            :model-value="sliderValue"
            :min="0"
            :max="sliderMax"
            :step="0.1"
            :disabled="sliderMax <= 0"
            :show-tooltip="false"
            @input="onSliderInput"
            @change="onSliderChange"
          />
          <div class="tts-hint">
            <span v-if="!ttsStore.streamEnded">已合成 {{ ttsStore.segTotal }} 段，可拖动范围随合成进度延伸</span>
            <span v-else>共 {{ ttsStore.segTotal }} 段，已全部合成</span>
          </div>
        </div>

        <el-button class="tts-btn-stop" circle @click="ttsStore.stop()">
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </div>
  </transition>
</template>

<script setup name="GlobalTtsBar">
import { VideoPlay, VideoPause, Close, Rank } from '@element-plus/icons-vue'
import useTtsStore from '@/store/modules/tts'

const ttsStore = useTtsStore()

// ==================== 可拖动悬浮窗 ====================
// pos=null 表示默认底部居中；用户拖动后记录自定义位置（localStorage 持久化）
const POS_KEY = 'tts_global_bar_pos'
const barEl = ref(null)
const pos = ref(null)
const dragging = ref(false)
let dragStart = null // { px, py, left, top, w, h }

const barStyle = computed(() => {
  if (pos.value) {
    return { left: pos.value.left + 'px', top: pos.value.top + 'px', bottom: 'auto', transform: 'none' }
  }
  return null
})

function loadPos() {
  try {
    const s = localStorage.getItem(POS_KEY)
    if (s) {
      const p = JSON.parse(s)
      if (p && typeof p.left === 'number' && typeof p.top === 'number') pos.value = p
    }
  } catch (e) { /* ignore */ }
}
function savePos() {
  try {
    if (pos.value) localStorage.setItem(POS_KEY, JSON.stringify(pos.value))
  } catch (e) { /* ignore */ }
}

function onPointerDown(e) {
  if (e.button !== 0) return
  // 仅在拖动把手上触发窗口拖动；进度条/按钮等交互区域完全不受影响
  if (!e.target.closest('.tts-drag-handle')) return
  const el = barEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  dragStart = { px: e.clientX, py: e.clientY, left: rect.left, top: rect.top, w: rect.width, h: rect.height }
  dragging.value = true
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
}

function onPointerMove(e) {
  if (!dragStart) return
  const vw = window.innerWidth
  const vh = window.innerHeight
  let left = dragStart.left + (e.clientX - dragStart.px)
  let top = dragStart.top + (e.clientY - dragStart.py)
  // 限定在视口内，避免拖出屏幕
  left = Math.min(Math.max(left, 8), vw - dragStart.w - 8)
  top = Math.min(Math.max(top, 8), vh - dragStart.h - 8)
  pos.value = { left, top }
}

function onPointerUp() {
  dragging.value = false
  dragStart = null
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  savePos()
}

loadPos()
onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
})

// ==================== 播放控制 ====================
// 拖动中不被播放进度覆盖
const dragSlider = ref(false)
const dragValue = ref(0)

const isSynthesizing = computed(() => ttsStore.phase === 'synthesizing')
// 只允许拖到已合成时长（未合成部分不可跳）
const sliderMax = computed(() => Math.max(0, Number(ttsStore.duration.toFixed(1))))
const sliderValue = computed(() => {
  if (dragSlider.value) return dragValue.value
  const v = Number(ttsStore.currentTime.toFixed(1))
  return v > sliderMax.value ? sliderMax.value : v
})

function onSliderInput(val) {
  dragSlider.value = true
  dragValue.value = val
}

function onSliderChange(val) {
  dragSlider.value = false
  ttsStore.seek(val)
}

function onToggle() {
  if (ttsStore.phase === 'playing') ttsStore.pause()
  else if (ttsStore.phase === 'paused') ttsStore.resume()
}

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec || 0))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r < 10 ? '0' : ''}${r}`
}
</script>

<style lang="scss" scoped>
.global-tts-bar {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  width: min(720px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  padding: 10px 14px;
  box-sizing: border-box;
  user-select: none;
}

.global-tts-bar.dragging {
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.18);
}

.tts-drag-handle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: #a8abb2;
  cursor: grab;
  touch-action: none;
  padding: 6px 4px;
}

.global-tts-bar.dragging .tts-drag-handle {
  cursor: grabbing;
}

.tts-bar-inner {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tts-btn-main,
.tts-btn-stop {
  flex-shrink: 0;
}

.tts-progress {
  flex: 1;
  min-width: 0;
}

.tts-progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  line-height: 18px;
}

.tts-seg-text {
  flex: 1;
  min-width: 0;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tts-time {
  flex-shrink: 0;
  color: #909399;
  font-variant-numeric: tabular-nums;
}

.tts-slider {
  margin: 0;
  :deep(.el-slider__runway) {
    height: 4px;
    margin: 8px 0;
  }
  :deep(.el-slider__bar) {
    height: 4px;
  }
  :deep(.el-slider__button) {
    width: 12px;
    height: 12px;
  }
}

.tts-hint {
  font-size: 11px;
  color: #a8abb2;
  line-height: 14px;
}

.tts-bar-fade-enter-active,
.tts-bar-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.tts-bar-fade-enter-from,
.tts-bar-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}

@media (max-width: 640px) {
  .global-tts-bar {
    bottom: 8px;
    padding: 8px 10px;
  }
  .tts-hint {
    display: none;
  }
}
</style>
